import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Video, Calendar, Download, Sparkles, ArrowRight } from './Icons';
import { bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, isToday, isTomorrow, addDays, startOfDay, isFuture } from 'date-fns';
import { useIsMobile } from '../hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';

// Rotating motivational phrases when no class is booked
const getMotivationalPhrase = () => {
  const phrases = [
    { main: "Ready to sweat?", sub: "Find your next workout!" },
    { main: "Miss us yet?", sub: "Your mat is waiting!" },
    { main: "Let's get moving!", sub: "Find your next class." },
    { main: "Time to hustle!", sub: "Crush your goals." },
    { main: "Feeling motivated?", sub: "Turn it into action!" },
    { main: "Your body will thank you.", sub: "Book a class today!" },
    { main: "No excuses today!", sub: "Find your fit." },
    { main: "Consistency is key.", sub: "Keep the momentum!" },
    { main: "Sweat now, shine later.", sub: "Book your session!" },
    { main: "You're stronger than you think.", sub: "Prove it!" },
    { main: "Progress, not perfection.", sub: "Every class counts." },
    { main: "Future you says thanks.", sub: "Book now!" },
    { main: "Make yourself proud.", sub: "Find your workout!" },
    { main: "The hardest part? Showing up.", sub: "We've got the rest." },
    { main: "Your journey continues.", sub: "Next chapter awaits!" },
    { main: "Energy begets energy.", sub: "Get moving!" },
    { main: "Self-care is not selfish.", sub: "Invest in yourself!" },
    { main: "One class at a time.", sub: "Start your streak!" },
    { main: "Champions train daily.", sub: "Stay consistent!" },
    { main: "Your goals miss you.", sub: "Get back on track!" },
  ];

  // Use date + hour based seed for variety throughout the day
  const now = new Date();
  const seed = now.toDateString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) + now.getHours();
  const index = seed % phrases.length;

  return phrases[index];
};

// Download ICS file for a class
function downloadCalendarEvent(schedule, classTitle, studio) {
  const dateStr = schedule.specificDate;
  const timeStr = schedule.startTime || '00:00';
  const [hours, minutes] = timeStr.split(':').map(Number);

  const startDate = parseISO(dateStr);
  startDate.setHours(hours, minutes, 0, 0);

  // Get duration from video or default to 1 hour (with archived fallback)
  const durationSeconds = schedule.video?.duration || schedule.archivedVideoDuration || schedule.liveDuration || 3600;
  const durationMs = durationSeconds * 1000;
  const endDate = new Date(startDate.getTime() + durationMs);

  const formatICSDate = (date) => date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  const className = classTitle || 'Class';
  const studioName = studio?.name || 'Studio';
  const studioAddress = studio?.address || '';
  const location = studioAddress || studioName;
  const instructor = schedule.classType === 'live'
    ? (schedule.liveInstructor ? `${schedule.liveInstructor.firstName || ''} ${schedule.liveInstructor.lastName || ''}`.trim() : '')
    : (schedule.video?.instructor ? `${schedule.video.instructor.firstName || ''} ${schedule.video.instructor.lastName || ''}`.trim() : '');
  const description = `${className}${instructor ? ` with ${instructor}` : ''} at ${studioName}${studioAddress ? `\\nAddress: ${studioAddress}` : ''}`;

  const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//MyStudioClass//Classes//EN
BEGIN:VEVENT
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
SUMMARY:${className}
DESCRIPTION:${description}
LOCATION:${location}
UID:${schedule.id}@mystudioclass
BEGIN:VALARM
ACTION:DISPLAY
DESCRIPTION:${className} starts in 1 hour
TRIGGER:-PT1H
END:VALARM
END:VEVENT
END:VCALENDAR`;

  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${className.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

// Countdown box component - premium glass effect
const CountdownBox = ({ value, label, showColon = true }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span
      style={{
        background: 'linear-gradient(145deg, var(--highlight-15) 0%, var(--highlight-8) 100%)',
        border: '1px solid var(--border-muted)',
        padding: '8px 12px',
        borderRadius: 10,
        fontSize: 18,
        fontWeight: 700,
        color: 'var(--text-primary)',
        fontFamily: "'JetBrains Mono', monospace",
        minWidth: 48,
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {String(value).padStart(2, '0')}
    </span>
    <span style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500, marginLeft: 6, marginRight: showColon ? 6 : 0 }}>{label}</span>
  </div>
);

export default function NextVideoCountdown() {
  const { user, studio, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Always render if authenticated, even if no user yet
  if (!isAuthenticated) {
    return null;
  }

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch user's bookings
  const { data: bookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false,
    refetchInterval: 60000, // Refetch every minute
  });

  // Find the next upcoming booked class
  const nextClass = useMemo(() => {
    if (!bookingsData?.data || !user?.id) return null;

    const now = currentTime;
    const bookings = bookingsData.data;
    let nextBooking = null;
    let nextClassTime = null;

    bookings.forEach(booking => {
      if (!booking.schedule || !booking.schedule.specificDate) return;

      const schedule = booking.schedule;
      const classDate = parseISO(schedule.specificDate);

      // Only consider future or today's classes
      if (!isFuture(classDate) && !isToday(classDate)) return;

      // Parse the start time
      const [hours, minutes] = schedule.startTime.split(':');
      const classDateTime = new Date(classDate);
      classDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // Only consider classes that haven't started yet
      if (classDateTime > now) {
        if (!nextClassTime || classDateTime < nextClassTime) {
          nextClassTime = classDateTime;
          nextBooking = booking;
        }
      }
    });

    return nextBooking ? { booking: nextBooking, time: nextClassTime } : null;
  }, [bookingsData, currentTime, user]);

  // Calculate countdown
  const countdown = useMemo(() => {
    if (!nextClass || !nextClass.time) return null;

    const diff = nextClass.time.getTime() - currentTime.getTime();

    if (diff <= 0) return null;

    const totalSeconds = Math.floor(diff / 1000);
    const totalHours = Math.floor(totalSeconds / 3600);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      days,
      hours,
      minutes,
      seconds,
      totalSeconds
    };
  }, [nextClass, currentTime]);

  // Format the class time display
  const classTimeDisplay = useMemo(() => {
    if (!nextClass || !nextClass.time) return null;

    const classTime = nextClass.time;
    const schedule = nextClass.booking.schedule;

    let dateText = '';
    if (isToday(classTime)) {
      dateText = 'Today';
    } else if (isTomorrow(classTime)) {
      dateText = 'Tomorrow';
    } else {
      dateText = format(classTime, 'EEEE, MMM d');
    }

    const timeText = format(classTime, 'h:mm a');
    const classTitle = schedule.classType === 'live'
      ? (schedule.liveTitle || 'Live Class')
      : (schedule.video?.title || 'Unknown Class');
    const isLive = schedule.classType === 'live';

    return { dateText, timeText, classTitle, isLive };
  }, [nextClass]);

  // Show loading state if query is still loading
  const isLoading = !bookingsData && !!user?.id;

  // Always show - either countdown or CTA
  const displayCountdown = countdown || { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const showCTA = !isLoading && !nextClass;

  // Determine color scheme based on class type
  const isLive = classTimeDisplay?.isLive;
  const colorVar = isLive ? 'primary' : 'secondary';

  return (
    <div
      style={{
        margin: isMobile ? '12px 16px 12px' : '16px 32px 20px',
        padding: isMobile ? '14px 18px' : '18px 28px',
        background: `linear-gradient(135deg, var(--${colorVar}-15) 0%, var(--${colorVar}-8) 50%, var(--bg-surface) 100%)`,
        borderRadius: 18,
        border: `1px solid var(--${colorVar}-20)`,
        boxShadow: `0 4px 20px rgba(0,0,0,0.1), 0 0 40px var(--${colorVar}-10)`,
        animation: 'fadeUp 0.4s ease',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top shine */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 1,
        background: `linear-gradient(90deg, transparent, var(--${colorVar}-30), var(--highlight-15), var(--${colorVar}-30), transparent)`,
      }} />
      {isMobile ? (
        /* Mobile Layout - Compact */
        <div className="flex flex-col gap-2">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</span>
            </div>
          ) : showCTA ? (
            /* CTA when no classes booked */
            (() => {
              const phrase = getMotivationalPhrase();
              return (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--secondary-20)' }}
                    >
                      <Sparkles className="w-4 h-4" style={{ color: 'var(--secondary)' }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        No classes booked yet
                      </p>
                      <p className="text-xs" style={{ color: 'var(--secondary)' }}>
                        {phrase.main}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/schedule')}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200"
                    style={{
                      background: 'var(--primary)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    Book Now
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })()
          ) : nextClass && classTimeDisplay ? (
            <>
              {/* Row 1: Class info */}
              <div className="flex items-center gap-2">
                {/* Type emblem with text */}
                <div
                  className="rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    padding: '6px 10px',
                    background: classTimeDisplay.isLive
                      ? 'linear-gradient(135deg, var(--primary), var(--primary-hover))'
                      : 'linear-gradient(135deg, var(--secondary), var(--secondary-hover))',
                    boxShadow: classTimeDisplay.isLive
                      ? '0 4px 12px var(--primary-30)'
                      : '0 4px 12px var(--secondary-30)',
                  }}
                >
                  <span style={{
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {classTimeDisplay.isLive ? 'Live' : 'Video'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {classTimeDisplay.classTitle}
                  </p>
                  <button
                    onClick={() => downloadCalendarEvent(nextClass.booking.schedule, classTimeDisplay.classTitle, studio)}
                    className="text-xs hover:underline"
                    style={{ color: 'var(--text-muted)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    {classTimeDisplay.dateText} at {classTimeDisplay.timeText}
                  </button>
                </div>
                <button
                  onClick={() => downloadCalendarEvent(nextClass.booking.schedule, classTimeDisplay.classTitle, studio)}
                  className="flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all duration-200"
                  style={{ color: `var(--${colorVar})`, background: `var(--${colorVar}-10)`, border: `1px solid var(--${colorVar}-20)` }}
                  title="Add to calendar"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Cal</span>
                </button>
              </div>

              {/* Row 2: Countdown */}
              <div className="flex items-center justify-center gap-1">
                <span className="text-xs mr-2" style={{ color: 'var(--text-muted)' }}>Starts in</span>
                {displayCountdown.days > 0 && (
                  <CountdownBox value={displayCountdown.days} label="d" />
                )}
                {(displayCountdown.days > 0 || displayCountdown.hours > 0) && (
                  <CountdownBox value={displayCountdown.hours} label="h" />
                )}
                <CountdownBox value={displayCountdown.minutes} label="m" showColon={displayCountdown.days === 0} />
                {displayCountdown.days === 0 && (
                  <CountdownBox value={displayCountdown.seconds} label="s" showColon={false} />
                )}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        /* Desktop Layout - matching Claude's sample exactly */
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Calendar size={20} style={{ color: 'var(--primary)' }} />
              <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Loading your classes...</span>
            </div>
          ) : showCTA ? (
            /* CTA when no classes booked */
            (() => {
              const phrase = getMotivationalPhrase();
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Sparkles size={20} style={{ color: 'var(--secondary)' }} />
                    <span style={{ color: 'var(--secondary)', fontSize: 14, fontWeight: 500 }}>{phrase.main}</span>
                    <span style={{ fontWeight: 500, fontSize: 14, color: 'var(--text-secondary)' }}>
                      You don't have any classes booked yet.
                    </span>
                  </div>
                  <button
                    onClick={() => navigate('/schedule')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 20px',
                      background: 'var(--primary)',
                      color: 'white',
                      borderRadius: 12,
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px var(--primary-30)',
                    }}
                  >
                    Browse Classes
                    <ArrowRight size={18} />
                  </button>
                </>
              );
            })()
          ) : nextClass && classTimeDisplay ? (
            <>
              {/* Left: Class info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Type emblem with text */}
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 10,
                    background: classTimeDisplay.isLive
                      ? 'linear-gradient(135deg, var(--primary), var(--primary-hover))'
                      : 'linear-gradient(135deg, var(--secondary), var(--secondary-hover))',
                    boxShadow: classTimeDisplay.isLive
                      ? '0 4px 12px var(--primary-30)'
                      : '0 4px 12px var(--secondary-30)',
                  }}
                >
                  <span style={{
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {classTimeDisplay.isLive ? 'Live' : 'Video'}
                  </span>
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 14, fontWeight: 500 }}>My Next Class:</span>
                <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>
                  {classTimeDisplay.classTitle}
                </span>
                <span style={{ color: 'var(--text-faint)' }}>|</span>
                <button
                  onClick={() => downloadCalendarEvent(nextClass.booking.schedule, classTimeDisplay.classTitle, studio)}
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: 14,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textDecoration: 'none',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  {classTimeDisplay.dateText} at {classTimeDisplay.timeText}
                </button>
                <button
                  onClick={() => downloadCalendarEvent(nextClass.booking.schedule, classTimeDisplay.classTitle, studio)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    background: `var(--${colorVar}-10)`,
                    border: `1px solid var(--${colorVar}-20)`,
                    borderRadius: 8,
                    color: `var(--${colorVar})`,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  title="Add to calendar"
                >
                  <Download size={14} />
                  Cal
                </button>
              </div>

              {/* Right: Countdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Starts in:</span>
                {displayCountdown.days > 0 && (
                  <CountdownBox value={displayCountdown.days} label="d" />
                )}
                {(displayCountdown.days > 0 || displayCountdown.hours > 0) && (
                  <CountdownBox value={displayCountdown.hours} label="h" />
                )}
                <CountdownBox value={displayCountdown.minutes} label="m" />
                {displayCountdown.days === 0 && (
                  <CountdownBox value={displayCountdown.seconds} label="s" showColon={false} />
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
